import Grid from "@mui/material/Grid2";
import { isEmpty } from "lodash";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { matchSorter } from "match-sorter";
import { SyntheticEvent, useEffect, useState } from "react";

import { ReAuthTabData } from "../../../models/TabSessionStorage";
import { ListOptions } from "../../../models/Master";
import {
  BenefitPlanDetails,
  EligibilityCheck,
  GetPatientInformationPayload,
  MemberInfoData,
  Patient,
  PatientInformation,
  PatientInformationView,
  SaveInformation,
} from "../../../models/Patient";
import { UserPageTrack } from "../../../models/User";
import {
  displayFlex,
  flexColumn,
  flexRow,
} from "../../../styles/mui/styles/display";
import { PatientDetailsContainer } from "../../../app/styles/styles";
import fontWeight from "../../../styles/mui/fontWeight";
import colors from "../../../styles/colors/colors";
import { FormInputText } from "../../../components/FormPriorAuthReAuth/FormInputText";
import { rules } from "../../../utils/validation/Validation";
import {
  eligibilityDefaultState,
  eligibilityFormInitialState,
  getType,
  HealthPlanData,
  NO_RECORD_FOUND,
} from "../../../constants/eligibility";
import DatePicker from "../../../components/FormPriorAuthReAuth/DatePicker";
import {
  AutoComplete,
  ButtonMinWidth,
  StyledPopperCheckBoxAutocomplete,
} from "../../PriorAuthWorkFlow/styles/style";
import useMenuItem from "../../../hooks/useMenuItem";
import {
  decryptId,
  getCurrnetPageFullPath,
  getHealthPlanValueType,
  getSessionStorage,
  getUaserPageViewPayload,
  getValue,
  isOptionEqualToValue,
} from "../../../utils";
import { ApplicationTitles } from "../../../constants/HomePage";
import { getValidMessageType } from "../../../constants/User";
import { redirectUri } from "../../../constants/Config";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import {
  BenefitPlanUidActionDispatchTypes,
  CreatePatientActionDispatchTypes,
  EligibilityCheckActionDispatchTypes,
  InformationActionDispatchTypes,
  MemberInfoDataActionDispatchTypes,
  PatientFromMbiActionDispatchTypes,
  PatientInformationByIdActionDispatchTypes,
} from "../../../constants/Patient";
import { EligibilityCheckResponse } from "../../../models/api/Patient";
import {
  GetPhysicianDetailsActionDispatchTypes,
  Numbers,
} from "../../../constants/Referral";
import { initialState } from "../../../reducers/PostCheckEligibility";
import { MyAccountModel } from "../../../models/MyAccount";
import { fontSize } from "../../../styles/mui/styles/font";
import { TabSessionStorageDispatchTypes } from "../../../constants/TabSessionStorage";
import {
  ReAuthDefaultReferralDetails,
} from "../../../models/Referral";
import PatientHealthPlanVerbiage from "../../PriorAuthWorkFlow/components/PatientHealthPlanVerbiage";
import { MemberInfo } from "./MemberInfo";
export interface PropsFromState {
  healthPlanState: ListOptions[];
  checkEligibilityData: EligibilityCheckResponse;
  patientFromMbiState: Patient;
  benefitPlanUidState: BenefitPlanDetails;
  loggedInUserState: MyAccountModel;
  patientInformationByIdState: PatientInformation;
  informationState: PatientInformationView;
  tabSessionStorage: ReAuthTabData;
  reAuthReferralDetailState: ReAuthDefaultReferralDetails;
  memberInfoDataState: MemberInfoData;
  informationErrorState: string | null;
  samlResponseState: any;
}

export interface PropsFromDispatch {
  resetStates: (actionType: string, field?: string) => void;
  getMasterHealthPlan: () => void;
  checkEligibility: (payload: EligibilityCheck) => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
  saveTabSessionStorage: (sessionData: ReAuthTabData) => void;
  getReAuthDefaultServiceProvider: (providerId: number) => void;
  getReAuthReferralData: (referralId: number) => void;
  getHealthPlanDetailsByBenefitPlanUid: (benefitPlanUid: string) => void;
  getPatientInformationById: (payload: GetPatientInformationPayload) => void;
  information: (payload: SaveInformation) => void;
  saveMemberInfo: (payload: MemberInfoData) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const ReAuthWorkFlow: React.FC<AllProps> = ({
  healthPlanState,
  getMasterHealthPlan,
  postUserPageTrack,
  checkEligibility,
  resetStates,
  reAuthReferralDetailState,
  getReAuthDefaultServiceProvider,
  getReAuthReferralData,
  checkEligibilityData,
  getHealthPlanDetailsByBenefitPlanUid,
  getPatientInformationById,
  benefitPlanUidState,
  loggedInUserState,
  patientInformationByIdState,
  information,
  saveMemberInfo,
  memberInfoDataState,
  informationState,
  informationErrorState,
}: AllProps) => {
  const [healthPlan, setHealthPlan] = useState<ListOptions | null>(
    HealthPlanData
  );
  const [openEligibilityCheck, setOpenEligibilityCheck] = useState(false);
  const [eligibilityPayload, setEligibilityPayload] =
    useState<EligibilityCheck>(eligibilityDefaultState);

  const { open, onOpen, onClose } = useMenuItem();
  const [warningType, setWarningType] = useState<number>(0);
  const [eligibilityData, setEligibilityData] =
    useState<EligibilityCheckResponse>(initialState);
  const methods = useForm<any>({
    defaultValues: eligibilityFormInitialState,
    mode: "all",
  });

  const { control, setValue, clearErrors, getValues, handleSubmit, reset } =
    methods;
  const values = getValues();
  const { referralId, providerId } = useParams();

  const actualReferralId = referralId
    ? !isEmpty(getSessionStorage("samlToken")) ? decryptId(referralId) : referralId
    : null;

  const actualProviderId = providerId
    ? !isEmpty(getSessionStorage("samlToken")) ? decryptId(providerId) : providerId
    : null;

  const onHealthPlanSelection = (
    _e: SyntheticEvent<Element, Event>,
    value: any
  ) => {
    onClose();
    setHealthPlan(value);
    setValue("healthPlan", value ? value.value : "");
    setWarningType(value ? getType(value) : 0);
    clearErrors("healthPlan");
  };
  const handleKeyDown = (event: any) => {
    if (event.key === " " && !event.target.value.trim()) {
      event.preventDefault();
    }
  };
  const handleCheckEligibility = () => {
    resetAPIResponse();
    setTimeout(() => {
      setOpenEligibilityCheck(false);
      const payload = {
        lastName: getValues("lastName"),
        dob: formatDate(getValues("dateOfBirth"), DateFormats.YYYY_MM_DD),
        policyId: getValues("subscriberId"),
        payerId: getValues("healthPlan")
          ? getHealthPlanValueType(getValues("healthPlan"), 0)
          : "",
        payerName: getValues("healthPlan")
          ? getHealthPlanValueType(getValues("healthPlan"), 1)
          : "",
      };
      setEligibilityPayload(payload);
      checkEligibility(payload);
    }, 0);
  };
  const handleReset = () => {
    setOpenEligibilityCheck(false);
    setHealthPlan(null);
    setWarningType(0);
    setEligibilityData(initialState);
    resetAPIResponse();
    reset({ values, keepDefaultValue: true });
  };
  const resetAPIResponse = () => {
    [
      EligibilityCheckActionDispatchTypes.ELIGIBILITY_CHECK_RESET,
      PatientFromMbiActionDispatchTypes.PATIENT_FROM_MBI_RESET,
      PatientInformationByIdActionDispatchTypes.PATIENT_INFORMATION_BY_ID_RESET,
      InformationActionDispatchTypes.INFORMATION_RESET,
      CreatePatientActionDispatchTypes.CREATE_PATIENT_RESET,
      TabSessionStorageDispatchTypes.SET_TAB_DATA_RESET,
      MemberInfoDataActionDispatchTypes.MEMBER_INFO_DATA_RESET,
      BenefitPlanUidActionDispatchTypes.BENEFIT_PLAN_UID_RESET,
      GetPhysicianDetailsActionDispatchTypes.GET_PHYSICIAN_DETAILS_RESET,
    ].forEach((actionType) => resetStates(actionType));
  };
  const setDefaultValues = () => {
    if (getValue(reAuthReferralDetailState, "referralId")) {
      reset({
        ...getValues(),
        lastName: (reAuthReferralDetailState.patientLastName || '').replace(/(^\s+|\s+$)/g, ""),
        dateOfBirth: formatDate(
          reAuthReferralDetailState.patientDOB,
          DateFormats.MM_DD_YYYY
        ),
        subscriberId: reAuthReferralDetailState?.subscriberId,
      });
    }
  };
  const setHealthPlanAsPerDefaultValue = (payerName: string) => {
    const selectedHealthPlan =
      healthPlanState &&
      healthPlanState.filter((item: ListOptions) => item.label === payerName);
    return selectedHealthPlan && selectedHealthPlan.length > Numbers.ZERO
      ? selectedHealthPlan[0]
      : null;
  };
  useEffect(() => {
    if (
      healthPlanState.length > Numbers.ZERO &&
      !isEmpty(reAuthReferralDetailState?.eligibilityPayerName)
    ) {
      const selectedHealthPlan = setHealthPlanAsPerDefaultValue(
        reAuthReferralDetailState?.eligibilityPayerName
      );
      setHealthPlan(selectedHealthPlan);
      onHealthPlanSelection(
        {} as SyntheticEvent<Element, Event>,
        selectedHealthPlan
      );
    } else {
      reset({
        ...getValues(),
        healthPlan: null,
      });
    }
  }, [reAuthReferralDetailState]);

  useEffect(() => {
    if (
      getValue(reAuthReferralDetailState, "referralId") > 0 &&
      Number(actualProviderId) > 0
    ) {
      getReAuthDefaultServiceProvider(Number(actualProviderId));
      setDefaultValues();
    }

    if(referralId && Number(referralId) > 0 && providerId && Number(providerId) > 0) {
      setDefaultValues();
    }
  }, [reAuthReferralDetailState]);

  useEffect(() => {
    handleReset();
    getMasterHealthPlan();
    if (Number(actualReferralId) > 0) {
      getReAuthReferralData(Number(actualReferralId));
    }
    postUserPageTrack(
      getUaserPageViewPayload(
        getValidMessageType(ApplicationTitles.CREATE_PRIOR_AUTH),
        getCurrnetPageFullPath(),
        redirectUri,
        ""
      )
    );
  }, []);

  useEffect(() => {
    if (
      checkEligibilityData &&
      getValue(
        checkEligibilityData,
        "response.additionalNotes.eligibilityState"
      ) > Numbers.ZERO
    ) {
      setEligibilityData(checkEligibilityData);
      setOpenEligibilityCheck(true);
      if (
        !isEmpty(
          getValue(
            checkEligibilityData,
            "response.additionalNotes.benefitPlanUid"
          )
        )
      ) {
        getHealthPlanDetailsByBenefitPlanUid(
          getValue(
            checkEligibilityData,
            "response.additionalNotes.benefitPlanUid"
          )
        );
      }
      return;
    }
    setOpenEligibilityCheck(false);
  }, [checkEligibilityData]);

  return (
    <>
      <Grid container spacing={2} mt={4} sx={PatientDetailsContainer}>
        <Grid size={{ xs: 12 }} sx={flexColumn}>
          <Grid container spacing={2} sx={flexRow} p={2}>
            <Grid size={{ xs: 12 }} sx={flexColumn}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid>
                  <Typography variant="h5" fontWeight={fontWeight[5]}>
                    REFERRAL DETAILS
                  </Typography>
                </Grid>
                <Grid>
                  <Typography
                    pt={1}
                    variant="body2"
                    fontWeight={fontWeight[4]}
                    color={colors.grey[2]}
                    textAlign="right"
                  >
                    NOTE: Authorizations will only be backdated up to 72 hours
                    from date of receipt.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid size={{ xs: 4 }}></Grid>
          </Grid>
          <Divider sx={{ mx: 2 }} />
          <Grid>
            <Grid container spacing={2} sx={flexRow} p={2}>
              <Grid size={{ xs: 2 }}>
                <FormInputText
                  name="lastName"
                  label="Last Name:"
                  control={control}
                  helper={rules.eligibilityLastNameTextRegex}
                  disabled={true}
                />
              </Grid>
              <Grid>
                <FormControl variant="standard">
                  <DatePicker
                    name="dateOfBirth"
                    control={control}
                    label={"Date of Birth:"}
                    helper={rules.dateOfBirthRequired}
                    allowFutureDate={false}
                    disabled={true}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 2 }}>
                <FormInputText
                  name="subscriberId"
                  label="Subscriber ID:"
                  control={control}
                  helper={rules.subscriberId}
                />
              </Grid>
              <Grid size={{ xs: 2 }}>
                <Box sx={displayFlex}>
                  <Typography
                    variant="body2"
                    fontWeight={fontWeight[4]}
                    color={colors.fonts[2]}
                  >
                    Health Plan:
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={fontWeight[2]}
                    color={colors.red[100]}
                    pl={"0.3rem"}
                  >
                    *
                  </Typography>
                </Box>
                <Controller
                  render={({ fieldState: { error } }) => (
                    <>
                      <Autocomplete
                        sx={AutoComplete}
                        options={healthPlanState}
                        freeSolo
                        forcePopupIcon
                        getOptionLabel={(option: string | ListOptions) =>
                          typeof option === "string" ? option : option.label
                        }
                        open={open}
                        onOpen={() => onOpen()}
                        onClose={() => onClose()}
                        PopperComponent={StyledPopperCheckBoxAutocomplete}
                        filterOptions={(options: any, { inputValue }) =>
                          matchSorter(options, inputValue, {
                            keys: ["label"],
                          })
                        }
                        isOptionEqualToValue={(option, value) =>
                          isOptionEqualToValue(option, value)
                        }
                        id="auto-complete"
                        autoComplete
                        disableClearable
                        clearIcon={""}
                        onChange={onHealthPlanSelection}
                        includeInputInList
                        value={healthPlan ?? undefined}
                        noOptionsText={NO_RECORD_FOUND}
                        data-testid="health-plan"
                        renderInput={(params: any) => {
                          params.inputProps.maxLength = 50;
                          return (
                            <Box mt={0.7}>
                              <TextField
                                {...params}
                                variant="standard"
                                placeholder="Choose"
                                onKeyDown={handleKeyDown}
                                inputProps={{
                                  ...params.inputProps,
                                  maxLength: 50,
                                  style: { fontSize: fontSize[12] },
                                }}
                              />
                            </Box>
                          );
                        }}
                        renderOption={(props, option) => {
                          const { key, ...Itemprops } = props;
                          return (
                            <Typography
                              key={key}
                              {...Itemprops}
                              variant="body1"
                              fontWeight={fontWeight[4]}
                              color={colors.fonts[25]}
                            >
                              {option.label}
                            </Typography>
                          );
                        }}
                      />
                      {error && (
                        <FormHelperText>{error.message}</FormHelperText>
                      )}
                    </>
                  )}
                  control={control}
                  name="healthPlan"
                  rules={rules.checkEligibilityHealthPlan}
                />
              </Grid>
              <Grid mt={1}>
                <Button
                  variant="contained"
                  onClick={handleSubmit(handleCheckEligibility)}
                  sx={ButtonMinWidth}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {
        <PatientHealthPlanVerbiage
          warningType={warningType}
          openEligibilityCheck={openEligibilityCheck}
          eligibilityData={eligibilityData}
        />
      }
      {openEligibilityCheck && (
        <Grid container spacing={2} mt={1} sx={PatientDetailsContainer}>
          <MemberInfo
            checkEligibilityState={getValue(checkEligibilityData, "response")}
            benefitPlanUidState={benefitPlanUidState}
            loggedInUserState={loggedInUserState}
            patientInformationByIdState={patientInformationByIdState}
            information={information}
            saveMemberInfo={saveMemberInfo}
            memberInfoDataState={memberInfoDataState}
            informationState={informationState}
            resetAPIResponse={resetAPIResponse}
            eligibilityPayload={eligibilityPayload}
            referralId={Number(actualReferralId)}
            patientId={reAuthReferralDetailState.patientId}
            providerId={Number(actualProviderId)}
            informationErrorState={informationErrorState}
            getPatientInformationById={getPatientInformationById}
          />
        </Grid>
      )}
    </>
  );
};

export default ReAuthWorkFlow;
